import { useAuth } from 'react-oidc-context';
import {
  StepUpInitiateAuthRequest,
  StepUpInitiateAuthResponse,
  StepUpRespondToChallengeRequest,
  StepUpRespondToChallengeResponse,
  AssociateSoftwareTokenResponse,
  ValidateSoftwareTokenResponse,
} from '../../_types';
import { baseApiUrl, fetcher, handleError, ServerErrorResponse } from 'data_sources/constants';
import AuthUtil from 'utils/AuthUtil';

export default function useStepUp() {
  const auth = useAuth();

  const initiateAuth = async (
    orgId: string,
    data: StepUpInitiateAuthRequest,
    callback?: (data: StepUpInitiateAuthResponse) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexid/step-up/v1/organizations/${orgId}/initiate-auth`;

    try {
      const accessToken = auth.user?.access_token || '';

      if (!accessToken) {
        throw new Error('No access token available');
      }

      await fetcher({
        method: 'post',
        url,
        body: data,
        options: {
          headers: {
            Authorization: accessToken,
          },
        },
      })
        .then((data) => {
          if (callback) callback(data);
        })
        .catch((err) => {
          console.error('Initiate auth error:', err);
          if (onError) handleError(err, { onError });
        });
    } catch (err) {
      console.error('Authentication error:', err);
      if (onError) handleError(err as Error, { onError });
    }
  };

  const respondToChallenge = async (
    orgId: string,
    data: StepUpRespondToChallengeRequest,
    callback?: (data: StepUpRespondToChallengeResponse) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexid/step-up/v1/organizations/${orgId}/respond-to-challenge`;

    try {
      const token = AuthUtil.getToken();

      if (!token) {
        throw new Error('No token available');
      }

      await fetcher({
        method: 'post',
        url,
        body: data,
        options: {
          headers: {
            Authorization: token,
          },
        },
      })
        .then((data) => {
          if (callback) callback(data);
        })
        .catch((err) => {
          console.error('Respond to challenge error:', err);
          if (onError) handleError(err, { onError });
        });
    } catch (err) {
      console.error('Authentication error:', err);
      if (onError) handleError(err as Error, { onError });
    }
  };

  const associateSoftwareToken = async (
    callback?: (data: AssociateSoftwareTokenResponse) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexid/admin-user/v1/associate-software-token`;
    try {
      const token = AuthUtil.getToken();

      if (!token) {
        throw new Error('No token available');
      }

      await fetcher({
        method: 'post',
        url,
        options: {
          headers: {
            Authorization: token,
          },
        },
      })
        .then((data) => {
          if (callback) callback(data);
        })
        .catch((err) => {
          console.error('Associate software token error:', err);
          if (onError) handleError(err, { onError });
        });
    } catch (err) {
      console.error('Authentication error:', err);
      if (onError) handleError(err as Error, { onError });
    }
  };

  const validateSoftwareToken = async (
    code: string,
    callback?: (data: ValidateSoftwareTokenResponse) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexid/admin-user/v1/validate-software-token`;
    try {
      const token = AuthUtil.getToken();

      if (!token) {
        throw new Error('No token available');
      }

      await fetcher({
        method: 'post',
        url,
        body: { code },
        options: {
          headers: {
            Authorization: token,
          },
        },
      })
        .then((data) => {
          if (callback) callback(data);
        })
        .catch((err) => {
          console.error('Validate software token error:', err);
          if (onError) handleError(err, { onError });
        });
    } catch (err) {
      console.error('Authentication error:', err);
      if (onError) handleError(err as Error, { onError });
    }
  };

  return {
    initiateAuth,
    respondToChallenge,
    associateSoftwareToken,
    validateSoftwareToken,
  };
}
